import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Users, KeyRound, LogOut as LogOutIcon, ShieldOff, Copy, Building, ShieldCheck, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";

const ROLES = ["admin", "analyst", "viewer"];

export default function AdminUsers() {
  const { isSuperAdmin } = useAuth();
  const superAdmin = isSuperAdmin();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [quota, setQuota] = useState(null);
  const [showAllTenants, setShowAllTenants] = useState(false);
  const [catalog, setCatalog] = useState({ all: [], roles: {} });
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showPwd, setShowPwd] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "analyst", tenant_id: "", extra_permissions: [], denied_permissions: [] });

  const load = async () => {
    try {
      const params = superAdmin && showAllTenants ? { all_tenants: true } : {};
      const [u, c] = await Promise.all([
        api.get("/admin/users", { params }),
        api.get("/admin/users/permissions-catalog"),
      ]);
      setUsers(u.data);
      setCatalog(c.data);
      // Quota strip — always fetched (also for super-admin against active tenant)
      try {
        const q = await api.get("/admin/users/quota");
        setQuota(q.data);
      } catch (_e) { setQuota(null); }
      if (superAdmin) {
        try {
          const t = await api.get("/platform/tenants");
          setTenants(t.data.tenants || []);
        } catch (_e) { /* not super */ }
      }
    } catch (_e) {
      toast.error("Failed to load users");
    }
  };
  useEffect(() => { load(); }, [showAllTenants]);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!superAdmin) delete payload.tenant_id;
      if (superAdmin && !payload.tenant_id) delete payload.tenant_id;
      const { data } = await api.post("/admin/users", payload);
      setShowCreate(false);
      setForm({ email: "", name: "", role: "analyst", tenant_id: "", extra_permissions: [], denied_permissions: [] });
      setShowPwd({ email: data.email, pwd: data.temp_password });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  const saveEdit = async () => {
    try {
      const body = {
        role: showEdit.role,
        disabled: showEdit.disabled,
        extra_permissions: showEdit.extra_permissions,
        denied_permissions: showEdit.denied_permissions,
      };
      if (superAdmin && showEdit.platform_role) {
        body.platform_role = showEdit.platform_role;
      }
      await api.patch(`/admin/users/${showEdit.id}`, body);
      toast.success("User updated");
      setShowEdit(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const resetPwd = async (u) => {
    if (!confirm(`Reset password for ${u.email}?`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`, {});
      setShowPwd({ email: u.email, pwd: data.temp_password });
      load();
    } catch (_e) { toast.error("Reset failed"); }
  };

  const forceLogout = async (u) => {
    if (!confirm(`Force logout ${u.email}? All active sessions will be invalidated.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/force-logout`);
      toast.success("Sessions invalidated");
      load();
    } catch (_e) { toast.error("Force logout failed"); }
  };

  const forceDisableMfa = async (u) => {
    if (!confirm(`Force-disable MFA on ${u.email}?\n\nThis is a BREAK-GLASS action for troubleshooting. The user will lose their authenticator secret, any recovery codes, and every existing session (all their tokens are invalidated). Ask them to log in fresh with just their password.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/disable-mfa`);
      toast.success(data?.message || `MFA disabled for ${u.email}`, { duration: 6000 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to disable MFA"); }
  };


  const requireMFA = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { must_setup_mfa: true });
      toast.success("MFA required on next login");
      load();
    } catch (_e) { toast.error("Failed"); }
  };

  const togglePerm = (target, perm, currentList) => {
    const set = new Set(currentList);
    if (set.has(perm)) set.delete(perm); else set.add(perm);
    setShowEdit({ ...showEdit, [target]: Array.from(set) });
  };

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
            <Users className="w-3.5 h-3.5" /> Team & permissions
          </div>
          <h1 className="font-display font-black text-5xl mt-2">Users</h1>
          <p className="text-[#8E9196] mt-1">Create analysts, tune per-user permissions, reset passwords, invalidate sessions.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="new-user-btn"
          className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-4 py-2.5 rounded-sm font-mono uppercase text-xs tracking-widest">
          <Plus className="w-4 h-4" /> New user
        </button>
      </header>

      {quota && (
        <div className="mb-4 border border-white/10 bg-[#0a0a0a] rounded-sm px-4 py-3 flex items-center gap-6 flex-wrap" data-testid="quota-strip">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#B57BFF]">Seat caps</div>
          {["admin", "analyst", "viewer"].map((r) => {
            const cap = quota.limits?.[r];
            const used = quota.usage?.[r] ?? 0;
            const isUnlimited = cap === -1 || cap == null;
            const isBlocked = cap === 0;
            const pct = isUnlimited || isBlocked ? 0 : Math.min(100, Math.round((used / cap) * 100));
            const color = isBlocked ? "#52525B" : pct >= 90 ? "#FF6B35" : pct >= 60 ? "#FFC542" : "#00F0FF";
            return (
              <div key={r} className="flex items-center gap-2" data-testid={`quota-${r}`}>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">{r}</span>
                <span className="text-sm font-mono" style={{ color }}>
                  {used}{isUnlimited ? " · ∞" : isBlocked ? " · blocked" : `/${cap}`}
                </span>
                {!isUnlimited && !isBlocked && (
                  <div className="w-16 h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {superAdmin && (
        <div className="mb-4 flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-[#8E9196]" data-testid="all-tenants-toggle-label">
            <input type="checkbox" checked={showAllTenants}
                   onChange={(e) => setShowAllTenants(e.target.checked)}
                   data-testid="all-tenants-toggle" />
            Show users across ALL tenants
          </label>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#52525B]">
            ({users.length} {showAllTenants ? "total" : "in active tenant"})
          </span>
        </div>
      )}

      <div className="border border-white/10 bg-[#121212] rounded-sm overflow-x-auto" data-testid="users-table">
        <table className="w-full text-sm min-w-[1080px]">
          <thead>
            <tr className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Role</th>
              {superAdmin && <th className="text-left px-5 py-3">Tenant</th>}
              <th className="text-left px-5 py-3">Perms</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Last login</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {[...users].sort((a, b) => (b.frozen ? 1 : 0) - (a.frozen ? 1 : 0)).map((u) => (
              <tr key={u.id} className={`border-b border-white/5 hover:bg-white/[0.02] ${u.frozen ? "bg-[#B57BFF]/[0.04]" : ""}`} data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3 font-mono text-xs">
                  <div className="flex items-center gap-2">
                    {u.frozen && (
                      <span title="Frozen — this account is protected and cannot be modified or deleted"
                        data-testid={`frozen-badge-${u.id}`}
                        className="inline-flex items-center gap-1 text-[#B57BFF]">
                        <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
                      </span>
                    )}
                    <span>{u.email}</span>
                  </div>
                </td>
                <td className="px-5 py-3">{u.name}</td>
                <td className="px-5 py-3 font-mono text-[10px] uppercase tracking-widest">
                  <span className="text-[#00F0FF]">{u.role}</span>
                  {u.platform_role === "super_admin" && (
                    <span className="ml-1.5 text-[#B57BFF]" title="Super admin">· platform</span>
                  )}
                  {u.frozen && (
                    <span className="ml-1.5 inline-flex items-center gap-1 border border-[#B57BFF]/50 text-[#B57BFF] px-1.5 py-0.5"
                      title="Cannot be deleted, demoted, disabled, or renamed">
                      <Lock className="w-2.5 h-2.5" /> frozen
                    </span>
                  )}
                </td>
                {superAdmin && (
                  <td className="px-5 py-3 text-xs" data-testid={`user-tenant-${u.id}`}>
                    <div className="flex items-center gap-1.5">
                      <Building className="w-3 h-3 text-[#B57BFF]" strokeWidth={1.75} />
                      <span className="font-mono text-[11px]">{u.tenant?.name || u.tenant_id || "—"}</span>
                    </div>
                  </td>
                )}
                <td className="px-5 py-3 font-mono text-xs">
                  {u.effective_permissions.length}
                  {u.extra_permissions.length > 0 && <span className="text-[#34C759] ml-1">+{u.extra_permissions.length}</span>}
                  {u.denied_permissions.length > 0 && <span className="text-[#FF3B30] ml-1">−{u.denied_permissions.length}</span>}
                </td>
                <td className="px-5 py-3">
                  {u.disabled ? (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">disabled</span>
                  ) : u.must_setup_mfa ? (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFCC00]">MFA required</span>
                  ) : (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#34C759]">active</span>
                  )}
                </td>
                <td className="px-5 py-3 font-mono text-[10px] text-[#52525B]">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "never"}</td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex gap-1 justify-end">
                    <button title={u.frozen ? "Frozen — cannot be modified" : "Edit"} onClick={() => !u.frozen && setShowEdit({ ...u })}
                      disabled={u.frozen}
                      className={`p-1.5 ${u.frozen ? "text-[#52525B] cursor-not-allowed" : "text-[#8E9196] hover:text-[#00F0FF]"}`}>Edit</button>
                    <button title={u.frozen ? "Frozen — cannot rotate remotely" : "Reset password"} onClick={() => !u.frozen && resetPwd(u)}
                      disabled={u.frozen}
                      className={`p-1.5 ${u.frozen ? "text-[#52525B] cursor-not-allowed" : "text-[#8E9196] hover:text-[#FFCC00]"}`}
                      data-testid={`reset-${u.id}`}>
                      <KeyRound className="w-3.5 h-3.5" />
                    </button>
                    <button title={u.frozen ? "Frozen — cannot force MFA" : "Require MFA"} onClick={() => !u.frozen && requireMFA(u)}
                      disabled={u.frozen}
                      className={`p-1.5 ${u.frozen ? "text-[#52525B] cursor-not-allowed" : "text-[#8E9196] hover:text-[#FFCC00]"}`}>MFA</button>
                    <button title={u.frozen ? "Frozen — cannot force logout" : "Force logout"} onClick={() => !u.frozen && forceLogout(u)}
                      disabled={u.frozen}
                      className={`p-1.5 ${u.frozen ? "text-[#52525B] cursor-not-allowed" : "text-[#8E9196] hover:text-[#FF3B30]"}`}
                      data-testid={`logout-${u.id}`}>
                      <LogOutIcon className="w-3.5 h-3.5" />
                    </button>
                    {!u.frozen && (
                      <button
                        title={
                          u.mfa_enabled
                            ? "Force disable MFA (break-glass) — user will log in with password only"
                            : u.must_setup_mfa
                              ? "Clear the 'must set up MFA' requirement so the user can log in"
                              : "Force disable MFA — clears any MFA state on this account"
                        }
                        onClick={() => forceDisableMfa(u)}
                        data-testid={`disable-mfa-${u.id}`}
                        className="p-1.5 text-[#8E9196] hover:text-[#B57BFF]">
                        <ShieldOff className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CREATE MODAL */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="New user" testid="create-user-modal">
          <form onSubmit={createUser} className="space-y-3">
            {[
              { key: "email", label: "Email", type: "email", placeholder: "user@corp.com" },
              { key: "name", label: "Full name", type: "text", placeholder: "Jane Doe" },
            ].map((f) => (
              <FormField key={f.key} label={f.label}>
                <input required type={f.type} value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  data-testid={`create-${f.key}`}
                  className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
              </FormField>
            ))}
            <FormField label="Role">
              <div className="flex items-center border border-white/15 rounded-sm overflow-hidden">
                {ROLES.map((r) => (
                  <button key={r} type="button" onClick={() => setForm({ ...form, role: r })}
                    data-testid={`create-role-${r}`}
                    className={`flex-1 py-2 font-mono text-[10px] uppercase tracking-widest ${form.role === r ? "bg-white/10 text-white" : "text-[#8E9196] hover:text-white"}`}>{r}</button>
                ))}
              </div>
            </FormField>
            {superAdmin && (
              <FormField label="Tenant">
                <select
                  value={form.tenant_id}
                  onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
                  data-testid="create-tenant"
                  className="w-full bg-[#0a0a0a] border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]"
                >
                  <option value="">Active tenant (from switcher)</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} · {t.plan}</option>
                  ))}
                </select>
              </FormField>
            )}
            {superAdmin && (
              <label className="flex items-start gap-2 text-xs text-[#8E9196] border border-[#B57BFF]/30 bg-[#B57BFF]/[0.04] rounded-sm p-2.5">
                <input type="checkbox"
                  checked={form.platform_role === "super_admin"}
                  onChange={(e) => setForm({ ...form, platform_role: e.target.checked ? "super_admin" : "tenant" })}
                  data-testid="create-super-admin" className="mt-0.5" />
                <span>
                  <span className="text-[#B57BFF] font-mono uppercase tracking-widest text-[10px]">Platform super-admin</span>
                  <div className="mt-0.5">Grants access to <code>/platform/*</code> — tenants, seat caps, platform mail defaults. Use sparingly.</div>
                </span>
              </label>
            )}
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B]">A temporary password will be generated and shown once.</div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowCreate(false)} className="border border-white/15 text-[#8E9196] hover:text-white px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">Cancel</button>
              <button data-testid="create-user-submit" className="border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">Create</button>
            </div>
          </form>
        </Modal>
      )}

      {/* EDIT MODAL */}
      {showEdit && (
        <Modal onClose={() => setShowEdit(null)} title={`Edit ${showEdit.email}`} testid="edit-user-modal" wide>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Role">
                <select value={showEdit.role} onChange={(e) => setShowEdit({ ...showEdit, role: e.target.value })}
                  className="w-full bg-[#0a0a0a] border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </FormField>
              <FormField label="Status">
                <button onClick={() => setShowEdit({ ...showEdit, disabled: !showEdit.disabled })}
                  className={`w-full py-2 rounded-sm font-mono text-xs uppercase tracking-widest border ${showEdit.disabled ? "border-[#FF3B30] text-[#FF3B30]" : "border-[#34C759] text-[#34C759]"}`}>
                  {showEdit.disabled ? "Disabled — click to enable" : "Active — click to disable"}
                </button>
              </FormField>
            </div>
            {superAdmin && !showEdit.frozen && (
              <label className="flex items-start gap-2 text-xs text-[#8E9196] border border-[#B57BFF]/30 bg-[#B57BFF]/[0.04] rounded-sm p-2.5">
                <input type="checkbox"
                  checked={showEdit.platform_role === "super_admin"}
                  onChange={(e) => setShowEdit({ ...showEdit, platform_role: e.target.checked ? "super_admin" : "tenant" })}
                  data-testid="edit-super-admin" className="mt-0.5" />
                <span>
                  <span className="text-[#B57BFF] font-mono uppercase tracking-widest text-[10px]">Platform super-admin</span>
                  <div className="mt-0.5">Toggle to promote/demote this account. Cannot demote the last remaining super-admin.</div>
                </span>
              </label>
            )}

            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-2">Permission overrides</div>
              <div className="text-[10px] text-[#52525B] mb-3">
                Preset role permissions in <span className="text-[#00F0FF]">cyan</span>. Toggle <span className="text-[#34C759]">grant extra</span> or <span className="text-[#FF3B30]">deny</span>.
              </div>
              <div className="grid grid-cols-2 gap-1 max-h-80 overflow-y-auto border border-white/10 rounded-sm p-2">
                {catalog.all.map((p) => {
                  const roleHas = (catalog.roles[showEdit.role] || []).includes(p);
                  const isExtra = (showEdit.extra_permissions || []).includes(p);
                  const isDenied = (showEdit.denied_permissions || []).includes(p);
                  return (
                    <div key={p} className="flex items-center justify-between px-2 py-1 border-b border-white/[0.03]">
                      <span className={`font-mono text-[10px] ${roleHas ? "text-[#00F0FF]" : "text-[#8E9196]"}`}>{p}</span>
                      <div className="flex gap-1">
                        <button onClick={() => togglePerm("extra_permissions", p, showEdit.extra_permissions)}
                          className={`px-1.5 py-0.5 border rounded-sm text-[9px] font-mono ${isExtra ? "border-[#34C759] text-[#34C759] bg-[#34C759]/10" : "border-white/15 text-[#52525B] hover:text-white"}`}>+</button>
                        <button onClick={() => togglePerm("denied_permissions", p, showEdit.denied_permissions)}
                          className={`px-1.5 py-0.5 border rounded-sm text-[9px] font-mono ${isDenied ? "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/10" : "border-white/15 text-[#52525B] hover:text-white"}`}>−</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowEdit(null)} className="border border-white/15 text-[#8E9196] hover:text-white px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">Cancel</button>
              <button onClick={saveEdit} data-testid="edit-user-save" className="border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">Save changes</button>
            </div>
          </div>
        </Modal>
      )}

      {/* PWD REVEAL */}
      {showPwd && (
        <Modal onClose={() => setShowPwd(null)} title="Temporary password" testid="temp-pwd-modal">
          <p className="text-sm text-[#8E9196] mb-2">Share this with <span className="text-white">{showPwd.email}</span>. It will not be shown again.</p>
          <div className="border border-[#FFCC00]/50 bg-[#FFCC00]/[0.05] rounded-sm p-3 flex items-center gap-2">
            <code className="flex-1 font-mono text-lg text-[#FFCC00] break-all" data-testid="temp-password">{showPwd.pwd}</code>
            <button onClick={() => copyText(showPwd.pwd)} className="p-2 text-[#8E9196] hover:text-white"><Copy className="w-4 h-4" /></button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Modal({ onClose, title, testid, wide, children }) {
  return (
    // `overflow-y-auto` on the outer overlay is a fallback for tall content:
    // if the modal itself hits its max-h, the inner scroller handles it;
    // otherwise the overlay itself scrolls. `items-start` + `my-6` anchors
    // the top so we never render above the viewport.
    <div
      className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm overflow-y-auto p-4 sm:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
        className={`bg-[#121212] border border-white/15 rounded-sm w-full ${wide ? "max-w-2xl" : "max-w-md"} mx-auto my-4 sm:my-8 flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]`}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-3 border-b border-white/10 shrink-0">
          <div className="font-display text-xl">{title}</div>
          <button onClick={onClose} className="text-[#8E9196] hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0">{children}</div>
      </div>
    </div>
  );
}
